import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import _ from 'lodash';
import { Text, Stack, VStack, Box, Button, Card } from '@chakra-ui/react';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';
import CTACard from '../../../atoms/CTACard';

function Dropdown({ activeItem }) {
  const [activeCategory, setActiveCategory] = useState(null);
  const { tt } = useUiComponentsInternationalContext();

  const subCategoryItems =
    activeItem?.items.find(item => item.title === activeCategory)?.items || [];

  // Animations
  const containerVariant = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariant = {
    hidden: { opacity: 0, x: -50 },
    show: { opacity: 1, x: 0 },
  };

  const toggleCategory = _.debounce(title => {
    if (activeCategory === title) setActiveCategory(null);
    else setActiveCategory(title);
  }, 300);

  return (
    <Stack
      direction={{ base: 'column', lg: 'row' }}
      gap={4}
      h={{ base: 'sm', lg: 'lg' }}
      className="dropdown"
    >
      <VStack
        align={{ base: 'center', lg: 'flex-start' }}
        minW={{ base: '100%', lg: '18rem' }}
      >
        <VStack
          flex={1}
          w="full"
          align="flex-start"
          overflowY="scroll"
          spacing={4}
        >
          {activeItem.items.map(item => (
            <Text
              key={item.title}
              as="button"
              py={1.5}
              color={
                activeCategory === item.title ? 'brand.ink' : 'typography.light'
              }
              onMouseEnter={() => toggleCategory(item.title)}
              onKeyDown={e => {
                if (['Enter', 'Space', 'ArrowRight'].includes(e.code)) {
                  e.stopPropagation();
                  e.preventDefault();
                  toggleCategory(item.title);
                }
              }}
            >
              {item.title}
            </Text>
          ))}
        </VStack>
        <Button mt={1} size="md">
          {activeItem?.ctaText || `Shop All ${activeItem?.title} Products`}
        </Button>
      </VStack>
      {subCategoryItems.length > 0 && (
        <VStack
          align={{ base: 'center', md: 'flex-start' }}
          minW={{ base: '100%', md: '18rem' }}
        >
          <VStack
            as={motion.div}
            variants={containerVariant}
            initial="hidden"
            animate="show"
            flex={1}
            w="full"
            align="flex-start"
            overflowY="scroll"
            spacing={4}
            pr={4}
          >
            {subCategoryItems.map(({ title, _key }) => (
              <Card
                key={_key}
                as={motion.div}
                variants={itemVariant}
                minH="9rem"
                w="full"
                bg="brand.ink"
                justifyContent="flex-end"
                p="1rem"
              >
                <Text color="#fff">{title}</Text>
              </Card>
            ))}
          </VStack>
        </VStack>
      )}
      <Box w="full">
        <CTACard
          title={tt(s => s.navBarDeptComponents.constantBarkerTitleLabel)}
          actionLabel={tt(s => s.navBarDeptComponents.actionLabel)}
        />
      </Box>
    </Stack>
  );
}

Dropdown.propTypes = {
  activeItem: PropTypes.shape({
    title: PropTypes.string,
    menuType: PropTypes.string,
    _key: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
      })
    ),
    ctaText: PropTypes.string,
  }).isRequired,
};

export default Dropdown;
